import type { BossRevolveSceneContour } from './BossRevolveSceneResolver';
import type { BossRevolveAxisResolution, RevolveAxisValue } from './RevolveAxisResolver';

/**
 * 旋转轴与轮廓草图平面不兼容时的统一提示。
 */
export const REVOLVE_AXIS_PROFILE_PLANE_INCOMPATIBLE_MESSAGE = '所选旋转轴不在当前轮廓草图平面内，不能作为旋转轴。';

/**
 * 旋转轴与轮廓草图平面的兼容性失败原因。
 */
export type RevolveAxisProfilePlaneCompatibilityFailureReason =
    /** 旋转轴缺失或退化。 */
    | 'invalid-axis'
    /** 轮廓平面缺失或退化。 */
    | 'invalid-profile-plane'
    /** 旋转轴没有落在轮廓草图平面内。 */
    | 'axis-not-on-profile-plane';

/**
 * 旋转轴与轮廓草图平面的兼容性结果。
 */
export type RevolveAxisProfilePlaneCompatibilityResult = {
    /** 是否兼容。 */
    compatible: true;
} | {
    /** 是否兼容。 */
    compatible: false;
    /** 失败原因。 */
    reason: RevolveAxisProfilePlaneCompatibilityFailureReason;
    /** 面向宿主层的提示文案。 */
    message: string;
};

/**
 * 旋转轮廓平面。
 */
interface RevolveProfilePlane {
    /** 平面上一点。 */
    origin: [number, number, number];
    /** 平面单位法向。 */
    normal: [number, number, number];
}

/**
 * 旋转轴与轮廓草图平面兼容性校验器。
 *
 * 设计说明：
 * 1. SolidWorks 允许旋转轴来自其它草图或模型边；
 * 2. 真正的约束不是“同属一个草图”，而是“轴线必须落在 profile 的 2D 草图平面内”；
 * 3. 该类只表达选择合法性，不解析场景对象，也不执行旋转建模。
 */
export class RevolveAxisProfilePlaneCompatibilityValidator {
    /** 方向平行判定容差。 */
    private static readonly DIRECTION_TOLERANCE = 1e-7;
    /** 点到平面距离判定容差。 */
    private static readonly DISTANCE_TOLERANCE = 1e-6;
    /** 向量退化判定容差平方。 */
    private static readonly LENGTH_SQUARED_TOLERANCE = 1e-12;

    /**
     * 校验旋转轴是否能用于指定轮廓集合。
     *
     * @param input 校验输入。
     * @returns 兼容性结果。
     */
    public validate(input: {
        axis: BossRevolveAxisResolution | null | undefined;
        contours: BossRevolveSceneContour[];
    }): RevolveAxisProfilePlaneCompatibilityResult {
        const normalizedAxis = input.axis ? this.normalizeAxis(input.axis.axis) : null;
        if (!normalizedAxis) {
            return this.createFailure('invalid-axis');
        }

        for (const contour of input.contours) {
            const plane = this.resolveProfilePlane(contour);
            if (!plane) {
                return this.createFailure('invalid-profile-plane');
            }
            if (!this.isAxisOnPlane(normalizedAxis, plane)) {
                return this.createFailure('axis-not-on-profile-plane');
            }
        }

        return { compatible: true };
    }

    /**
     * 构建失败结果。
     *
     * @param reason 失败原因。
     * @returns 失败结果。
     */
    private createFailure(
        reason: RevolveAxisProfilePlaneCompatibilityFailureReason,
    ): RevolveAxisProfilePlaneCompatibilityResult {
        return {
            compatible: false,
            reason,
            message: REVOLVE_AXIS_PROFILE_PLANE_INCOMPATIBLE_MESSAGE,
        };
    }

    /**
     * 归一化旋转轴。
     *
     * @param axis 原始旋转轴。
     * @returns 归一化旋转轴；输入无效时返回 null。
     */
    private normalizeAxis(axis: RevolveAxisValue): RevolveAxisValue | null {
        if (!this.isFinitePoint(axis.point) || !this.isFinitePoint(axis.direction)) {
            return null;
        }
        const direction = this.normalizeVector(axis.direction);
        if (!direction) {
            return null;
        }
        return {
            point: [...axis.point],
            direction,
        };
    }

    /**
     * 从轮廓恢复草图平面。
     *
     * @param contour 旋转轮廓。
     * @returns 轮廓平面；轮廓无效时返回 null。
     */
    private resolveProfilePlane(contour: BossRevolveSceneContour): RevolveProfilePlane | null {
        const origin = contour.geometry.outer[0] ?? null;
        if (!origin || !this.isFinitePoint(origin) || !this.isFinitePoint(contour.geometry.normal)) {
            return null;
        }
        const normal = this.normalizeVector(contour.geometry.normal);
        if (!normal) {
            return null;
        }
        return {
            origin: [...origin],
            normal,
        };
    }

    /**
     * 判断轴线是否位于平面内。
     *
     * @param axis 归一化旋转轴。
     * @param plane 轮廓草图平面。
     * @returns 轴线位于平面内时返回 true。
     */
    private isAxisOnPlane(axis: RevolveAxisValue, plane: RevolveProfilePlane): boolean {
        // 轴方向必须平行于 profile 平面，否则只会穿过平面而不是位于平面内。
        const directionNormalDot = Math.abs(this.dot(axis.direction, plane.normal));
        if (directionNormalDot > RevolveAxisProfilePlaneCompatibilityValidator.DIRECTION_TOLERANCE) {
            return false;
        }

        // 轴上一点必须落在 profile 平面内，避免接受与平面平行但偏移的外部轴。
        const offset = this.subtract(axis.point, plane.origin);
        const signedDistance = Math.abs(this.dot(offset, plane.normal));
        return signedDistance <= RevolveAxisProfilePlaneCompatibilityValidator.DISTANCE_TOLERANCE;
    }

    /**
     * 判断三维点是否均为有限数字。
     *
     * @param point 三维点。
     * @returns 是否有效。
     */
    private isFinitePoint(point: [number, number, number]): boolean {
        return point.every((value) => Number.isFinite(value));
    }

    /**
     * 归一化三维向量。
     *
     * @param vector 原始向量。
     * @returns 单位向量；退化时返回 null。
     */
    private normalizeVector(vector: [number, number, number]): [number, number, number] | null {
        const lengthSquared = this.dot(vector, vector);
        if (lengthSquared <= RevolveAxisProfilePlaneCompatibilityValidator.LENGTH_SQUARED_TOLERANCE) {
            return null;
        }
        const length = Math.sqrt(lengthSquared);
        return [
            vector[0] / length,
            vector[1] / length,
            vector[2] / length,
        ];
    }

    /**
     * 计算三维向量点积。
     *
     * @param left 左向量。
     * @param right 右向量。
     * @returns 点积。
     */
    private dot(left: [number, number, number], right: [number, number, number]): number {
        return left[0] * right[0] + left[1] * right[1] + left[2] * right[2];
    }

    /**
     * 计算三维向量差。
     *
     * @param left 被减向量。
     * @param right 减向量。
     * @returns 差值。
     */
    private subtract(left: [number, number, number], right: [number, number, number]): [number, number, number] {
        return [
            left[0] - right[0],
            left[1] - right[1],
            left[2] - right[2],
        ];
    }
}
